'use client'

import * as React from 'react'
import Link from 'next/link'
import { ExternalLink, UserCog } from 'lucide-react'
import { remedyCopy } from '@/lib/ai/quota-copy'
import type { AiRemedy } from '@/lib/ai/credit-state'
import { cn } from '@/lib/utils'

/**
 * Renders exactly the remedies the SERVER sent — no more, no fewer, and it
 * derives nothing.
 *
 * ── The rule this enforces ──────────────────────────────────────────────────
 * A remedy must be one the caller can actually perform. `create:AiStudio` is a
 * workspace EDITOR grant; `manage:AiStudio` — required to add a provider key —
 * is ADMIN-only. So an editor who hits the ceiling must be told to ask a
 * workspace administrator, and must NOT be handed a deep link into a dialog
 * that will refuse them.
 *
 * The server does that filtering, because it is the only side that knows the
 * caller's abilities. This component therefore does NOT re-derive the list
 * from a local ability check: two sources of truth on a permission drift, and
 * the drift is invisible until a customer reports a dead-end link.
 *
 * An empty list renders nothing. "We could not confirm what you may do" is not
 * a licence to guess — `UNKNOWN_ALLOWANCE.remedies` is `[]` for that reason.
 *
 * `ask_admin` is deliberately NOT a link. `AI_REMEDY_COPY.ask_admin.href` is
 * null and `remedyCopy()` never fills it in, so the "no dead-end link" property
 * holds by construction rather than by review.
 */
export interface AiRemedyListProps {
  /** Ability-filtered server-side. Rendered verbatim. */
  remedies: AiRemedy[]
  /** Optional provider slug to highlight in the settings deep link. */
  provider?: string | null
  className?: string
}

export function AiRemedyList({
  remedies,
  provider,
  className,
}: AiRemedyListProps) {
  if (remedies.length === 0) return null

  return (
    <ul className={cn('flex flex-col items-start gap-1.5', className)}>
      {remedies.map((remedy) => {
        const copy = remedyCopy(remedy, provider)

        if (copy.href) {
          return (
            <li key={remedy}>
              <Link
                href={copy.href}
                data-testid={`ai-remedy-${remedy}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium underline underline-offset-4 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
              >
                <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                {copy.label}
              </Link>
            </li>
          )
        }

        return (
          <li key={remedy}>
            <span
              data-testid={`ai-remedy-${remedy}`}
              className="inline-flex items-center gap-1.5 text-sm"
            >
              <UserCog aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
              {copy.label} — {copy.description}
            </span>
          </li>
        )
      })}
    </ul>
  )
}
